import React, { useState, useEffect, useRef } from "react";
import { saveAs } from "file-saver";
import Menu from "../../../../components/layout/menu/index";
import { getSeasons, createSeason, updateSeason, deleteSeason } from "../../../../client/seasons";
import { uploadExcelSuppliersPerSeason } from "../../../../client/seasonDocumentSuppliers";
import ModalSuccess from "../../../../components/shared/modals/modalSuccess";
import ModalDelete from "../../../../components/shared/modals/modalDelete";
import DownloadDocumentButton from "../../../../components/shared/downloadDocument";
import Pagination from "../../../../components/shared/pagination";
import { axiosInstance } from "../../../../client";
import ModalAlertWarning from "../../../../components/shared/modals/modalAlertWarning";

function cx(...classes) { return classes.filter(Boolean).join(" "); }

function toInputDate(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  try { return new Date(value).toISOString().slice(0, 10); } catch { return ""; }
}

function Switch({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={cx(
        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none",
        checked ? "bg-blue-600" : "bg-gray-500"
      )}
      aria-pressed={checked}
      aria-label={label}
    >
      <span
        className={cx(
          "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-5" : "translate-x-1"
        )}
      />
    </button>
  );
}

export default function SeasonsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ name: "", enable: false, popup: false, start_Date: today, end_Date: today });

  const fileRef = useRef(null);
  const [importSeason, setImportSeason] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false)
  const [showModalSuccess, setShowModalSuccess] = useState(false);
  const [modalSuccessText, setModalSuccessText] = useState("");
  const [showModalDelete, setShowModalDelete] = useState(false);

  const [showModalAlertWarning, setShowModalAlertWarning] = useState(false);
  const [alertWarningError, setAlertWarningError] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth' 
  });
};


  async function loadSeasons() {
    try {
      setLoading(true);
      setError(null);
      const res = await getSeasons(page, pageSize);
      setRows(res.data.data);
      setTotalPages(res.data.totalPages);
      setTotalCount(res.data.totalCount);
    } catch (e) {
      console.error("Error al cargar temporadas:", e);
      
      let errorMessage = e?.response?.data?.message || 
                        e?.message || 
                        "Error al cargar las temporadas.";
      
      setAlertWarningError(errorMessage);
      setShowModalAlertWarning(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadSeasons(); }, [page, pageSize]);

  function openEdit(row) {
    setEditing(row);
    setForm({
      name: row.name,
      enable: !!row.enable,
      popup: !!row.popup,
      start_Date: toInputDate(row.start_Date),
      end_Date: toInputDate(row.end_Date),
    });
    setEditorOpen(true);
  }

  async function saveForm(e) {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      enable: form.enable,
      popup: form.popup,
      start_Date: form.start_Date,
      end_Date: form.end_Date,
    };
    if (!payload.name) return;
    if (!form.start_Date || !form.end_Date) { 
      setAlertWarningError("Completa las fechas");
      setShowModalAlertWarning(true);
      return; 
    }
    if (new Date(payload.end_Date) < new Date(payload.start_Date)) { 
      setAlertWarningError("La fecha fin debe ser mayor o igual a la fecha inicio");
      setShowModalAlertWarning(true);
      return; 
    }
      try {
      if (editing) {
        await updateSeason(editing.id, payload);
      } else {
        await createSeason(payload);
      }
      await loadSeasons();
      setEditorOpen(false);
      setModalSuccessText(`¡Su temporada fue ${editing ? 'editada' : 'agregada'} con éxito!`)
      setShowModalSuccess(true);
      resetForm();
    } catch (e) {
      console.error("Error al guardar:", e);
      
      let errorMessage = e?.response?.data?.message || 
                        e?.message || 
                        `Error al ${editing ? 'editar' : 'crear'} la temporada.`;
      
      setAlertWarningError(errorMessage);
      setShowModalAlertWarning(true);
    }
  }

  function resetForm() {
    setEditing(null);
    setForm({ name: "", enable: false, popup: false, start_Date: today, end_Date: today });
  }

  function handleConfirmDelete() {
    (async () => {
      try {
        await deleteSeason(deleting.id);
        await loadSeasons();
        setShowModalDelete(false);
        setModalSuccessText("¡Su temporada fue eliminada con éxito!");
        setShowModalSuccess(true);
      } catch (e) {
        console.error("Error al eliminar:", e);
        let errorMessage = e?.response?.data?.message || 
                          e?.message || 
                          "Error al eliminar la temporada.";
        
        setAlertWarningError(errorMessage);
        setShowModalAlertWarning(true);
      }
    })();
  }

  function updateRow(updated, options = {}) {
    if (options.openEditor) return openEdit(updated);
    (async () => {
      try {
        const payload = {
          name: updated.name,
          enable: updated.enable,
          popup: updated.popup,
          start_Date: updated.start_Date,
          end_Date: updated.end_Date,
        };
        await updateSeason(updated.id, payload);
        await loadSeasons();
        setModalSuccessText("¡Su temporada fue editada con éxito!");
        setShowModalSuccess(true);
      } catch (e) {
        console.error("Error al actualizar:", e);
        
        let errorMessage = e?.response?.data?.message || 
                          e?.message || 
                          "Error al actualizar la temporada.";
        
        setAlertWarningError(errorMessage);
        setShowModalAlertWarning(true);
      }
    })();
  }

  async function downloadTemplate() {
    try {
      const res = await axiosInstance.get("Documents/ExportExcelDocuments", {
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      let fileName = "Plantilla_Documentos.xlsx";
      const contentDisposition = res.headers["content-disposition"];

      if (contentDisposition) {
        const utf8Name = contentDisposition.match(/filename\*\=UTF-8''([^;]+)/);
        const normalName = contentDisposition.match(/filename="?([^";]+)"?/);

        if (utf8Name && utf8Name[1]) {
          fileName = decodeURIComponent(utf8Name[1]);
        } else if (normalName && normalName[1]) {
          fileName = normalName[1];
        }
      }

      if (!fileName.endsWith(".xlsx")) fileName += ".xlsx";

      saveAs(blob, fileName);
    } catch (err) {
      console.error("Error al descargar plantilla:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Error al descargar la plantilla de documentos.";
      alert(msg);
    }
  }
  
  function handleAskImport(season) {
    setImportSeason(season);
    fileRef.current?.click();
  }

  async function handleFilePicked(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !importSeason) return;

    try {
      setIsImporting(true);
      const res = await uploadExcelSuppliersPerSeason(importSeason.id, file);
      setModalSuccessText(res?.message || `¡Importación completada con éxito!`);
      setShowModalSuccess(true);
    } catch (err) {
      console.error("Error al subir Excel:", err);
      
      let errorMessage = err?.response?.data?.message || 
                        err?.message || 
                        "Error al subir el archivo Excel.";
      
      setAlertWarningError(errorMessage);
      setShowModalAlertWarning(true);
    } finally {
      setIsImporting(false);
      setImportSeason(null);
    }
  }

  function handleInlineToggle(row, field) {
    const updated = { ...row, [field]: !row[field] };
    updateRow(updated);
  }

  return (
    <div>
      <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
      <div className={`flex-1 pt-[67px] transition-all duration-300 ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"} bg-[#F7F8F9] w-full z-50 min-h-screen`}>
        <div className="md:pt-8 pt-4 pb-10 px-[3%] flex flex-col gap-4">
          <div className="flex flex-col items-start justify-between gap-3 md:items-center md:flex-row">
            <h1 className="text-blue font-itcbold md:text-[32px] text-2xl">
              Temporadas
            </h1>
            <DownloadDocumentButton
              text="Descargar Plantilla"
              onClick={() => downloadTemplate()}
            />
          </div>
          <div className="flex flex-col items-start justify-center gap-4 ">
            <div className="flex items-center justify-center w-full">
              <div className="bg-[#00769633] flex  md:flex-row flex-col items-center justify-center gap-6 w-full rounded-2xl p-4">
                <div className="flex flex-row  md:flex-col w-full md:w-[20%] justify-center gap-4 items-center md:items-start">
                  <p className="text-blue font-itcdemi text-[24px] text-left">
                    {editing ? 'Editar temporada' : 'Agregar temporada'}
                  </p>
                </div>
                <div className="flex flex-col md:flex-row items-center justify-between w-full md:w-[60%] gap-3 md:gap-8">
                  <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-12">
                    <div className="flex flex-col gap-2 md:col-span-6">
                      <p className="font-itcdemi text-[#1F1F1F] lg:text-[14px] text-[16px]">Nombre: <span className="text-red-500">*</span></p>
                      <div className="border w-full bg-white flex rounded-[6px] border-[#D1D5DB]">
                        <input
                          value={form.name}
                          onChange={(e) => {
                            const value = e.target.value;
                            const filteredValue = value.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s-]/g, '');
                            const limitedValue = filteredValue.slice(0, 50);
                            setForm(f => ({ ...f, name: limitedValue }));
                          }}                      
                          className="rounded-[6px] w-full font-itcbook outline-none p-2 placeholder:font-itcbook placeholder:text-[14px] text-[14px]"
                          placeholder="Escribir nombre..."
                        />
                      </div>
                    </div>
                    <div className="flex gap-8">
                      <div className="flex flex-col gap-2 md:col-span-3">
                        <p className="font-itcdemi text-[#1F1F1F] lg:text-[14px] text-[16px] mb-1">Habilitado:</p>
                        <div className="flex w-full ">
                          <Switch
                            checked={form.enable}
                            onChange={(v) => setForm(f => ({ ...f, enable: v }))}
                            label="Enable"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 md:col-span-3">
                        <p className="font-itcdemi text-[#1F1F1F] lg:text-[14px] text-[16px] mb-1">Popup:</p>
                        <div className="flex w-full ">
                          <Switch
                            checked={form.popup}
                            onChange={(v) => setForm(f => ({ ...f, popup: v }))}
                            label="Popup"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 md:col-span-6">
                      <p className="font-itcdemi text-[#1F1F1F] lg:text-[14px] text-[16px]">Fecha de inicio: <span className="text-red-500">*</span></p>
                      <div className="border w-full flex rounded-[6px] border-[#D1D5DB]">
                        <input 
                          type="date"
                          value={form.start_Date}
                          onChange={(e) => setForm(f => ({ ...f, start_Date: e.target.value, end_Date: e.target.value > f.end_Date ? e.target.value : f.end_Date }))}   
                          className="w-full px-3 py-2 text-sm border border-gray-300 outline-none rounded-xl focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 md:col-span-6">
                      <p className="font-itcdemi text-[#1F1F1F] lg:text-[14px] text-[16px]">Fecha de fin: <span className="text-red-500">*</span></p>
                      <div className="border w-full flex rounded-[6px] border-[#D1D5DB]">
                        <input 
                          type="date"
                          value={form.end_Date}
                          min={form.start_Date}
                          onChange={(e) => setForm(f => ({ ...f, end_Date: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 outline-none rounded-xl focus:ring-2 focus:ring-blue-500" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-row  md:flex-col justify-center items-center h-full w-full md:w-[30%] gap-6">
                  <button
                    onClick={saveForm}
                    disabled={(!form.name)}
                    className={`text-white bg-blue rounded-lg w-[161px] h-[50px] font-itcdemi text-[18px]
                      ${!form.name ? 'bg-gray cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    Guardar
                  </button>
                  <button
                    onClick={resetForm}
                    className={`text-blue w-[161px] h-[50px] font-itcdemi rounded-lg text-[18px] border-[2px] border-blue`}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
            <div className="w-full">
              {isLoading ? (
                <div>Cargando datos...</div>
              ) : (
                <>
                <div className="flex items-center mb-2 space-x-2 text-sm font-itcbook">
                  <p>Mostrar</p>
                  <div className="border-[#D1D5DB] border-[1px] rounded-md w-[58px] flex justify-center items-center h-[40px]">
                    <select
                      className="w-full h-full px-2 text-sm bg-white rounded-md outline-none cursor-pointer font-itcbook"
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    >
                      {[10, 15, 25, 50].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <p>Registros</p>
                </div>

                <div className="w-full overflow-hidden overflow-x-auto">
                  <table className="w-full bg-white border border-collapse table-fixed border-blue">
                    <thead>
                      <tr className='text-[14px] 2xl:text-[16px] leading-tight text-center font-itcbold'>
                        <th
                          className={`p-3 w-[80px] border text-center  border-[#D9D9D9]`}
                        >
                          #
                        </th>
                        <th
                          className={`p-3 w-[200px] border text-center  border-[#D9D9D9]`}
                        >
                          Nombre
                        </th>
                        <th
                          className={`p-3 border w-[150px] text-center  border-[#D9D9D9]`}
                        >
                          Habilitado
                        </th>
                        <th
                          className={`p-3 border w-[150px] text-center  border-[#D9D9D9]`}
                        >
                          Popup
                        </th>
                        <th
                          className={`p-3 border w-[150px] text-center  border-[#D9D9D9]`}
                        >
                          Fecha de inicio
                        </th>
                        <th
                          className={`p-3 border w-[150px] text-center  border-[#D9D9D9]`}
                        >
                          Fecha de fin
                        </th>
                        <th
                          className={`p-3 border w-[150px] text-center  border-[#D9D9D9]`}
                        >
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className='transition-all duration-300 ease-in-out'>
                      {rows.map((r, i) => {
                        return (
                          <tr className={`text-[13px] 2xl:text-[15px] p-3 border-collapse h-[62px] font-itcmedium text-left bg-white`}>
                            <td className="p-3 text-center border border-[#D9D9D9]">
                              {(page - 1) * pageSize + (i + 1)}
                            </td>
                            <td className="p-3 text-center border border-[#D9D9D9]">
                              {r.name}
                            </td>
                            <td className="p-3 text-center border border-[#D9D9D9]">
                              <Switch
                                checked={r.enable}
                                onChange={(v) => handleInlineToggle(r, "enable")}
                                label="Enable"
                              />
                            </td>
                            <td className="p-3 text-center border border-[#D9D9D9]">
                              <Switch
                                checked={r.popup}
                                onChange={(v) => handleInlineToggle(r, "popup")}
                                label="Popup"
                              />
                            </td>
                            <td className="p-3 text-center border border-[#D9D9D9]">
                              {new Date(r.start_Date).toLocaleString("en-GB", { timeZone: "UTC" }).split(",")[0]}
                            </td>
                            <td className="p-3 text-center border border-[#D9D9D9]">
                              {new Date(r.end_Date).toLocaleString("en-GB", { timeZone: "UTC" }).split(",")[0]}
                            </td>
                            <td className="p-3 text-center border border-[#D9D9D9]">
                              <div className="flex flex-col items-center justify-center gap-2">
                                <button
                                  onClick={() => {
                                    setEditing(r);
                                    setForm({ name: r.name, enable: r.enable, popup: r.popup, start_Date: r.start_Date, end_Date: r.end_Date })
                                    scrollToTop();
                                  }}
                                  className="p-2 flex justify-center items-center bg-[#EFEEF7] rounded-[4px] border-[#D9D9D9] border-[1px] w-[39px] h-[27px] hover:bg-blue hover:text-white transition-colors"
                                >
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.333 16.048L16.57 3.81004C17.0534 3.34741 17.6988 3.09245 18.3679 3.09976C19.037 3.10707 19.6767 3.37608 20.1499 3.84917C20.6231 4.32226 20.8923 4.96183 20.8998 5.63093C20.9073 6.30003 20.6525 6.94547 20.19 7.42904L7.951 19.667C7.6718 19.9463 7.31619 20.1366 6.929 20.214L3 21L3.786 17.07C3.86345 16.6828 4.05378 16.3272 4.333 16.048Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14.5 6.5L17.5 9.5" stroke="currentColor" strokeWidth="2" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => {
                                    setDeleting(r);
                                    setShowModalDelete(true);
                                  }}
                                  className="p-2 flex justify-center items-center bg-[#EFEEF7] rounded-[4px] border-[#D9D9D9] border-[1px] w-[39px] h-[27px] hover:bg-red hover:text-white transition-colors"
                                >
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12.7008 3.10083L12.945 4.79167H16.25C16.4158 4.79167 16.5747 4.85751 16.6919 4.97472C16.921 5.25091 16.875 5.41667 16.25 6.04167H15.6092L14.8817 14.5292C14.8375 15.0458 14.8017 15.4708 14.7442 15.8142C14.6858 16.1717 14.5967 16.4917 14.4225 16.7883C14.149 17.2543 13.7424 17.6278 13.255 17.8608C12.945 18.0083 12.6183 18.0692 12.2567 18.0975C11.9092 18.125 11.4833 18.125 10.965 18.125H9.035C8.51667 18.125 8.09083 18.125 7.74333 18.0975C7.38167 18.0692 7.055 18.0083 6.745 17.8608C6.25757 17.6278 5.85098 17.2543 5.5775 16.7883C5.4025 16.4917 5.315 16.1717 5.25583 15.8142C5.19833 15.47 5.1625 15.0458 5.11833 14.5292L4.39083 6.04167H3.75C3.58424 6.04167 3.42527 5.97582 3.30806 5.85861C3.19085 5.7414 3.125 5.58243 3.125 5.41667C3.125 5.25091 3.19085 5.09193 3.30806 4.97472C3.42527 4.85751 3.58424 4.79167 3.75 4.79167H7.055L7.29917 3.10083L7.30833 3.05C7.46 2.39167 8.025 1.875 8.73333 1.875H11.2667C11.975 1.875 12.54 2.39167 12.6917 3.05L12.7008 3.10083ZM8.3175 4.79167H11.6817L11.4683 3.31167C11.4283 3.1725 11.3267 3.125 11.2658 3.125H8.73417C8.67333 3.125 8.57167 3.1725 8.53167 3.31167L8.3175 4.79167ZM9.375 8.75C9.375 8.58424 9.30915 8.42527 9.19194 8.30806C9.07473 8.19085 8.91576 8.125 8.75 8.125C8.58424 8.125 8.42527 8.19085 8.30806 8.30806C8.19085 8.42527 8.125 8.58424 8.125 8.75V12.9167C8.125 13.0824 8.19085 13.2414 8.30806 13.3586C8.42527 13.4758 8.58424 13.5417 8.75 13.5417C8.91576 13.5417 9.07473 13.4758 9.19194 13.3586C9.30915 13.2414 9.375 13.0824 9.375 12.9167V8.75ZM11.875 8.75C11.875 8.58424 11.8092 8.42527 11.6919 8.30806C11.5747 8.19085 11.4158 8.125 11.25 8.125C11.0842 8.125 10.9253 8.19085 10.8081 8.30806C10.6908 8.42527 10.625 8.58424 10.625 8.75V12.9167C10.625 13.0824 10.6908 13.2414 10.8081 13.3586C10.9253 13.4758 11.0842 13.5417 11.25 13.5417C11.4158 13.5417 11.5747 13.4758 11.6919 13.3586C11.8092 13.2414 11.875 13.0824 11.875 12.9167V8.75Z" fill="currentColor" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => {
                                    handleAskImport(r);
                                  }}
                                  className="p-2 flex justify-center items-center bg-[#EFEEF7] rounded-[4px] border-[#D9D9D9] border-[1px] w-[39px] h-[27px] hover:bg-blue hover:text-white transition-colors"
                                >

                                  {isImporting && importSeason == r ? (
                                    <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                                      <path d="M272 112C272 85.5 293.5 64 320 64C346.5 64 368 85.5 368 112C368 138.5 346.5 160 320 160C293.5 160 272 138.5 272 112zM272 528C272 501.5 293.5 480 320 480C346.5 480 368 501.5 368 528C368 554.5 346.5 576 320 576C293.5 576 272 554.5 272 528zM112 272C138.5 272 160 293.5 160 320C160 346.5 138.5 368 112 368C85.5 368 64 346.5 64 320C64 293.5 85.5 272 112 272zM480 320C480 293.5 501.5 272 528 272C554.5 272 576 293.5 576 320C576 346.5 554.5 368 528 368C501.5 368 480 346.5 480 320zM139 433.1C157.8 414.3 188.1 414.3 206.9 433.1C225.7 451.9 225.7 482.2 206.9 501C188.1 519.8 157.8 519.8 139 501C120.2 482.2 120.2 451.9 139 433.1zM139 139C157.8 120.2 188.1 120.2 206.9 139C225.7 157.8 225.7 188.1 206.9 206.9C188.1 225.7 157.8 225.7 139 206.9C120.2 188.1 120.2 157.8 139 139zM501 433.1C519.8 451.9 519.8 482.2 501 501C482.2 519.8 451.9 519.8 433.1 501C414.3 482.2 414.3 451.9 433.1 433.1C451.9 414.3 482.2 414.3 501 433.1z" />
                                    </svg>
                                  ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <g clip-path="url(#clip0_1_18127)">
                                        <path d="M16.4405 15.1001C20.0405 14.7901 21.5105 12.9401 21.5105 8.8901V8.7601C21.5105 4.2901 19.7205 2.5001 15.2505 2.5001H8.74047C4.27047 2.5001 2.48047 4.2901 2.48047 8.7601V8.8901C2.48047 12.9101 3.93047 14.7601 7.47047 15.0901"
                                          stroke={"currentColor"} stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M12 22V9.12"
                                          stroke={"currentColor"} stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M15.3504 11.3496L12.0004 7.99961L8.65039 11.3496"
                                          stroke={"currentColor"} stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                      </g>
                                      <defs>
                                        <clipPath id="clip0_1_18127">
                                          <rect width="24" height="24" fill="white" transform="matrix(1 0 0 -1 0 24)" />
                                        </clipPath>
                                      </defs>
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFilePicked}
                    className="hidden"
                  />
                </div>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  changePage={setPage}
                  recordsPerPage={pageSize}
                  totalRecords={totalCount}
                />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <ModalSuccess
        showModalSuccess={showModalSuccess}
        closeModalSuccess={() => setShowModalSuccess(false)}
        fill="green"
        text={modalSuccessText}
      />

      <ModalDelete
        showModalDelete={showModalDelete}
        closeModal={() => setShowModalDelete(false)}
        accept={handleConfirmDelete}
        textType="temporada"
        pronoun="la"
      />
      
      <ModalAlertWarning
        showModalAlertWarning={showModalAlertWarning}
        closeModal={() => setShowModalAlertWarning(false)}
        error={alertWarningError}
        accept={() => setShowModalAlertWarning(false)}
      />

    </div>
  )

}
